import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    TableContainer,
    Paper,
    Alert,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Pagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAllUsers } from "../../Apis/UsersApi";

const GetAllUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");

    const [page, setPage] = useState(1); // Pagination starts from 1
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const navigate = useNavigate();

    const fetchUsers = async (search = "") => {
        try {
            setLoading(true);
            const data = await getAllUsers(search);
            setUsers(data?.users || []);
            setError(null);
            setPage(1); // Reset to first page when fetching
        } catch (err) {
            setError("Failed to fetch users.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleChangePage = (event, value) => {
        setPage(value);
    };

    const startIdx = (page - 1) * rowsPerPage;
    const endIdx = startIdx + rowsPerPage;
    const paginatedUsers = users.slice(startIdx, endIdx);

    const totalPages = Math.ceil(users.length / rowsPerPage) || 1;

    return (
        <Box sx={{ padding: 3, width: "90%" }}>
            {/* Search Input + Button */}
            <Box sx={{ display: "flex", gap: 5, mb: 3, justifyContent: "space-between" }}>
                <TextField
                    fullWidth
                    label="Search users by name or mobile"
                    variant="outlined"
                    value={searchText}
                    size="small"
                    sx={{ width: "200px" }}
                    onChange={(e) => setSearchText(e.target.value)}
                />
                <Button
                    variant="contained"
                    onClick={() => fetchUsers(searchText)}
                    size="small"
                    sx={{ width: "150px", minHeight: "32px" }}
                >
                    Search
                </Button>
            </Box>

            {/* Loader */}
            {loading && <CircularProgress />}

            {/* Error */}
            {error && <Alert severity="error">{error}</Alert>}

            {/* Users Table */}
            {!loading && users.length > 0 && (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Mobile</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedUsers.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.name || "N/A"}</TableCell>
                                        <TableCell>{user.email || "N/A"}</TableCell>
                                        <TableCell>{user.mobile || "N/A"}</TableCell>
                                        <TableCell>
                                            <Button onClick={() => navigate(`/users/${user.id}`)}>View</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination controls */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 2,
                        }}
                    >
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                                {[5, 10, 20, 50].map((num) => (
                                    <MenuItem key={num} value={num}>
                                        {num} Rows
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handleChangePage}
                            color="primary"
                        />
                    </Box>
                </>
            )}

            {!loading && users.length === 0 && (
                <Typography sx={{ textAlign: "center" }}>No users found.</Typography>
            )}
        </Box>
    );
};

export default GetAllUsers;
